import React, { createContext, useContext, useState, useCallback } from 'react';

export type TimezoneMode = 'UTC' | 'MYT';

interface TimezoneContextValue {
  timezone: TimezoneMode;
  toggleTimezone: () => void;
  isExternallyControlled: boolean;
}

const TimezoneContext = createContext<TimezoneContextValue>({
  timezone: 'UTC',
  toggleTimezone: () => {},
  isExternallyControlled: false,
});

function getInitialTimezone(): { timezone: TimezoneMode; isExternallyControlled: boolean } {
  const path = window.location.pathname.toLowerCase();
  if (path === '/utc') return { timezone: 'UTC', isExternallyControlled: true };
  if (path === '/myt') return { timezone: 'MYT', isExternallyControlled: true };
  return { timezone: 'MYT', isExternallyControlled: false };
}

export function TimezoneProvider({ children }: { children: React.ReactNode }) {
  const [initial] = useState(getInitialTimezone);
  const [timezone, setTimezone] = useState<TimezoneMode>(initial.timezone);

  const toggleTimezone = useCallback(() => {
    if (initial.isExternallyControlled) return;
    setTimezone(prev => (prev === 'UTC' ? 'MYT' : 'UTC'));
  }, [initial.isExternallyControlled]);

  return (
    <TimezoneContext.Provider value={{ timezone, toggleTimezone, isExternallyControlled: initial.isExternallyControlled }}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone() {
  return useContext(TimezoneContext);
}
